import React, { useState, useCallback, useEffect } from 'react';
import DateRangeSelector from '../../components/Planification/DateRangeSelector';
import PlanningTable from '../../components/Planification/PlanningTable';
import LoadingSpinner from '../../components/Planification/LoadingSpinner';
import ErrorMessage from '../../components/Planification/ErrorMessage';
import { fetchPlanification } from '../../services/planificationService';
import { usePlanification } from '../../context/PlanificationContext';
import './Planification.css';

const Planification = () => {
  const {
    planificationData,
    isLoading,
    error,
    lastParams,
    updatePlanificationData,
    hasDataForParams,
    setIsLoading,
    setError,
  } = usePlanification();

  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Afficher les données en cache au chargement de la page
  useEffect(() => {
    if (planificationData && lastParams) {
      console.log('📦 Données en cache disponibles pour:', lastParams);
    }
  }, [planificationData, lastParams]);

  const handleFetchPlanning = useCallback(async (dateDebut, dateFin) => {
    const params = { dateDebut, dateFin };

    // Toujours charger les données depuis l'API (même si en cache)
    // pour récupérer les modifications faites dans Zoho
    setLocalLoading(true);
    setIsLoading(true);
    setLocalError(null);
    setError(null);

    try {
      console.log('🔄 Chargement des données depuis l\'API...');
      const data = await fetchPlanification(dateDebut, dateFin);
      updatePlanificationData(data, params);
      console.log('✅ Données chargées et mises en cache');
    } catch (err) {
      const errorMessage = err.message || 'Erreur lors du chargement des données';
      setLocalError(errorMessage);
      setError(errorMessage);
      console.error('❌ Erreur:', errorMessage);
    } finally {
      setLocalLoading(false);
      setIsLoading(false);
    }
  }, [updatePlanificationData, setIsLoading, setError]);

  const displayLoading = isLoading || localLoading;
  const displayError = error || localError;

  return (
    <div className="planification-page">
      <div className="page-header">
        <h1 className="page-title">
          <svg className="title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7"></rect>
            <rect x="14" y="3" width="7" height="7"></rect>
            <rect x="14" y="14" width="7" height="7"></rect>
            <rect x="3" y="14" width="7" height="7"></rect>
          </svg>
          Planification
        </h1>
        <p className="page-description">Consultez la planification des équipes et des projets</p>
      </div>

      <DateRangeSelector 
        onFetchPlanning={handleFetchPlanning} 
        loading={displayLoading}
        initialDateDebut={lastParams?.dateDebut}
        initialDateFin={lastParams?.dateFin}
      />

      {/* Badge indiquant que les données sont en cache */}
      {planificationData && lastParams && !displayLoading && (
        <div className="cache-info">
          <svg className="cache-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <span>Planification pour la période du: {lastParams.dateDebut} - {lastParams.dateFin}</span>
        </div>
      )}

      {displayLoading && (
        <div className="loading-container-with-message">
          <LoadingSpinner 
            message="Récupération des données depuis Zoho..." 
            showTimer={true}
          />
          <div className="loading-info">
            <p className="loading-warning">⏳ Cette opération peut prendre 2 à 5 minutes</p>
            <p className="loading-tip">Le serveur traite un grand volume de données. Merci de patienter...</p>
          </div>
        </div>
      )}
      
      {displayError && <ErrorMessage message={displayError} />}
      
      {planificationData && !displayLoading && !displayError && (
        <PlanningTable data={planificationData} />
      )}

      {!planificationData && !displayLoading && !displayError && (
        <div className="empty-state">
          <svg className="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          <p>Sélectionnez une période pour afficher le planning</p>
        </div>
      )}
    </div>
  );
};

export default Planification;
